/**
 * useLoginComplianceCheck Hook
 *
 * Checks for compliance issues on app load/login and sends email notifications.
 * Runs once per session to avoid notification spam.
 *
 * CHECKS PERFORMED:
 * 1. Vessel certificates expiring in 30/7/0 days
 * 2. Crew certificates expiring in 30/7/0 days
 * 3. Overdue safety drills (7/14/30 days overdue)
 * 4. Overdue maintenance (7/30/90 days overdue)
 *
 * TRIGGER LOGIC:
 * - Only checks on initial app load (not every re-render)
 * - Uses sessionStorage to prevent duplicate checks
 * - Sends only most critical items (one per category)
 * - Fire-and-forget pattern (doesn't block UI)
 *
 * USAGE:
 * ```javascript
 * // In TailwindApp.js or DatabaseApp component
 * function DatabaseApp() {
 *   useLoginComplianceCheck();
 *   // ... rest of component
 * }
 * ```
 */

import { useEffect } from 'react';
import { useNotifications } from './useNotifications';
import { useUpcomingEvents } from './useUpcomingEvents';
import { useAuth } from '../components/tailwind/auth/AuthGate';
import { useVessel } from '../contexts/VesselContext';

export const useLoginComplianceCheck = () => {
  const { sendNotification } = useNotifications();
  const { events } = useUpcomingEvents();
  const { user: currentUser } = useAuth();
  const { currentVessel } = useVessel();

  useEffect(() => {
    // Only check if we have required data
    if (!currentUser || !currentVessel || !events) {
      return;
    }

    // Only check once per session (prevents duplicate notifications)
    const hasChecked = sessionStorage.getItem('compliance_check_done');
    if (hasChecked) {
      return;
    }

    // Mark as checked immediately to prevent race conditions
    sessionStorage.setItem('compliance_check_done', 'true');

    // Run compliance check asynchronously (non-blocking)
    checkComplianceAndNotify();

  }, [events, currentUser, currentVessel]); // eslint-disable-line react-hooks/exhaustive-deps

  /**
   * Check all compliance items and send notifications for critical issues
   */
  const checkComplianceAndNotify = async () => {
    const notifications = [];

    try {
      // Get recipient email(s) - use current user for now
      // TODO: Fetch organization key holders from members table
      const recipients = getRecipients();
      if (recipients.length === 0) {
        console.warn('[ComplianceCheck] No recipients found, skipping notifications');
        return;
      }

      // Filter events by type
      const vesselCerts = events.filter(e => e.type === 'certificate' && e.module === 'certificates');
      const crewCerts = events.filter(e => e.type === 'certificate' && e.module === 'crew');
      const drills = events.filter(e => e.type === 'drill');
      const maintenance = events.filter(e => e.type === 'equipment');

      // 1. CHECK VESSEL CERTIFICATES (critical: <= 7 days or expired)
      const criticalVesselCerts = vesselCerts.filter(cert => {
        const daysUntil = cert.daysUntil !== undefined ? cert.daysUntil :
          cert.isOverdue ? -cert.daysOverdue : 0;
        return daysUntil <= 7;
      });

      if (criticalVesselCerts.length > 0) {
        // Send notification for most critical (soonest to expire)
        const mostCritical = criticalVesselCerts.reduce((prev, current) => {
          const prevDays = prev.daysUntil !== undefined ? prev.daysUntil :
            prev.isOverdue ? -prev.daysOverdue : 0;
          const currentDays = current.daysUntil !== undefined ? current.daysUntil :
            current.isOverdue ? -current.daysOverdue : 0;
          return currentDays < prevDays ? current : prev;
        });

        const daysUntil = mostCritical.daysUntil !== undefined ? mostCritical.daysUntil :
          mostCritical.isOverdue ? -mostCritical.daysOverdue : 0;

        notifications.push({
          type: 'CERT_EXPIRY_VESSEL',
          data: {
            certificateName: mostCritical.title || 'Certificate',
            vesselName: currentVessel.name || 'Vessel',
            expiryDate: mostCritical.date,
            daysUntil: Math.max(0, daysUntil),
            certificateType: mostCritical.certificateType,
            issuingAuthority: mostCritical.issuingAuthority
          },
          recipients
        });
      }

      // 2. CHECK CREW CERTIFICATES (critical: <= 7 days or expired)
      const criticalCrewCerts = crewCerts.filter(cert => {
        const daysUntil = cert.daysUntil !== undefined ? cert.daysUntil :
          cert.isOverdue ? -cert.daysOverdue : 0;
        return daysUntil <= 7;
      });

      if (criticalCrewCerts.length > 0) {
        // Send notification for most critical
        const mostCritical = criticalCrewCerts.reduce((prev, current) => {
          const prevDays = prev.daysUntil !== undefined ? prev.daysUntil :
            prev.isOverdue ? -prev.daysOverdue : 0;
          const currentDays = current.daysUntil !== undefined ? current.daysUntil :
            current.isOverdue ? -current.daysOverdue : 0;
          return currentDays < prevDays ? current : prev;
        });

        const daysUntil = mostCritical.daysUntil !== undefined ? mostCritical.daysUntil :
          mostCritical.isOverdue ? -mostCritical.daysOverdue : 0;

        // Parse crew name from title (format: "John Smith - Certificate Name")
        const titleParts = (mostCritical.title || '').split(' - ');
        const crewName = titleParts[0] || 'Crew Member';
        const certName = titleParts[1] || 'Certificate';

        notifications.push({
          type: 'CERT_EXPIRY_CREW',
          data: {
            crewName,
            certificateName: certName,
            vesselName: currentVessel.name || 'Vessel',
            expiryDate: mostCritical.date,
            daysUntil: Math.max(0, daysUntil),
            crewRole: mostCritical.crewRole,
            certificateNumber: mostCritical.certificateNumber
          },
          recipients
        });
      }

      // 3. CHECK OVERDUE DRILLS (any overdue)
      const overdueDrills = drills.filter(drill => drill.isOverdue);

      if (overdueDrills.length > 0) {
        // Send notification for most overdue
        const mostOverdue = overdueDrills.reduce((prev, current) =>
          (current.daysOverdue || 0) > (prev.daysOverdue || 0) ? current : prev
        );

        notifications.push({
          type: 'DRILL_OVERDUE',
          data: {
            drillName: mostOverdue.title || 'Safety Drill',
            vesselName: currentVessel.name || 'Vessel',
            daysOverdue: mostOverdue.daysOverdue || 0,
            lastCompleted: mostOverdue.lastCompleted,
            frequencyDays: mostOverdue.frequencyDays,
            drillType: mostOverdue.drillType
          },
          recipients
        });
      }

      // 4. CHECK OVERDUE MAINTENANCE (any overdue)
      const overdueMaintenance = maintenance.filter(job => job.isOverdue);

      if (overdueMaintenance.length > 0) {
        // Send notification for most overdue
        const mostOverdue = overdueMaintenance.reduce((prev, current) =>
          (current.daysOverdue || 0) > (prev.daysOverdue || 0) ? current : prev
        );

        notifications.push({
          type: 'MAINT_OVERDUE',
          data: {
            maintenanceName: mostOverdue.title || 'Maintenance Job',
            vesselName: currentVessel.name || 'Vessel',
            daysOverdue: mostOverdue.daysOverdue || 0,
            lastCompleted: mostOverdue.lastCompleted,
            equipmentName: mostOverdue.equipmentName,
            category: mostOverdue.category,
            interval: mostOverdue.interval
          },
          recipients
        });
      }

      // SEND ALL NOTIFICATIONS (fire-and-forget)
      if (notifications.length > 0) {
        console.log(`[ComplianceCheck] Sending ${notifications.length} compliance notification(s)`);

        // Send notifications in parallel (non-blocking)
        notifications.forEach(({ type, data, recipients }) => {
          sendNotification(type, data, recipients).catch(err => {
            console.warn(`[ComplianceCheck] Failed to send ${type}:`, err);
          });
        });
      } else {
        console.log('[ComplianceCheck] No critical compliance issues found');
      }

    } catch (error) {
      console.error('[ComplianceCheck] Error during compliance check:', error);
    }
  };

  /**
   * Get recipient email addresses
   * TODO: Fetch from organization members with ADMIN/MASTER roles
   */
  const getRecipients = () => {
    const emails = [];

    // Add current user email if available
    if (currentUser?.email) {
      emails.push(currentUser.email);
    }

    // TODO: Query organization members table for key holders
    // const orgMembers = await supabase
    //   .from('organization_members')
    //   .select('email, role')
    //   .eq('organization_id', currentVessel.organizationId)
    //   .in('role', ['ADMIN', 'MASTER']);

    return emails;
  };
};
